<template>
  <article class="md-body">
    <template v-for="(part, i) in parts" :key="i">
      <MermaidDiagram v-if="part.type === 'mermaid'" :code="part.code" />
      <JourneyArcs v-else-if="part.type === 'arcs'" :code="part.code" />
      <ApiContract v-else-if="part.type === 'api-contract'" :code="part.code" />
      <PreferenceCard v-else-if="part.type === 'preferences'" :code="part.code" />
      <div v-else class="md-prose" v-html="part.html" />
    </template>
  </article>
</template>

<script setup>
import { computed } from 'vue'
import MarkdownIt from 'markdown-it'
import MermaidDiagram from './MermaidDiagram.vue'
import JourneyArcs from './JourneyArcs.vue'
import ApiContract from './ApiContract.vue'
import PreferenceCard from './PreferenceCard.vue'

const md = new MarkdownIt({
  html: false,
  linkify: true,
  typographer: true,
  breaks: false,
})

// Open standalone HTML assets, linked reference images, and any
// external links in a new tab so clicking a link never navigates away from the
// slide deck mid-presentation.
const defaultLinkOpen =
  md.renderer.rules.link_open ||
  ((tokens, idx, options, env, self) => self.renderToken(tokens, idx, options))
md.renderer.rules.link_open = (tokens, idx, options, env, self) => {
  const href = tokens[idx].attrGet('href') || ''
  if (/\.(?:html|png|jpe?g|gif|webp|svg)($|[?#])/i.test(href) || /^https?:/i.test(href)) {
    tokens[idx].attrSet('target', '_blank')
    tokens[idx].attrSet('rel', 'noopener noreferrer')
  }
  return defaultLinkOpen(tokens, idx, options, env, self)
}

const props = defineProps({
  source: { type: String, default: '' },
})

/*
 * Split the projected content into prose, ```mermaid blocks, and ```arcs
 * blocks, ```api-contract request/response examples, and ```preferences cards.
 * Everything else goes through markdown-it.
 */
const parts = computed(() => {
  const src = props.source || ''
  const out = []
  const re = /```(mermaid|arcs|api-contract|preferences)\s*\n([\s\S]*?)```/g
  let last = 0
  let m
  while ((m = re.exec(src)) !== null) {
    if (m.index > last) {
      const text = src.slice(last, m.index).trim()
      if (text) out.push({ type: 'md', html: md.render(text) })
    }
    out.push({ type: m[1], code: m[2] })
    last = re.lastIndex
  }
  if (last < src.length) {
    const text = src.slice(last).trim()
    if (text) out.push({ type: 'md', html: md.render(text) })
  }
  return out
})
</script>

<style scoped>
.md-body {
  font-size: 1.02rem;
  line-height: 1.6;
}

.md-prose :deep(h2) {
  font-size: 1.25rem;
  margin: 1.4rem 0 0.6rem;
}

.md-prose :deep(h3) {
  font-size: 1.1rem;
  margin: 1.2rem 0 0.5rem;
}

.md-prose :deep(p) {
  margin: 0.6rem 0;
}

.md-prose :deep(ul),
.md-prose :deep(ol) {
  margin: 0.6rem 0;
  padding-left: 1.3rem;
}

.md-prose :deep(li) {
  margin-bottom: 0.4rem;
}

.md-prose :deep(a) {
  color: var(--teal-deep);
}

/* Inline code — used for the `Journey:` phase badge and term chips. */
.md-prose :deep(code) {
  background: var(--paper-warm);
  border: 1px solid var(--border);
  border-radius: 4px;
  padding: 0.05em 0.4em;
  font-size: 0.9em;
}

/* War-story / key-idea callouts use blockquotes. */
.md-prose :deep(blockquote) {
  margin: 1rem 0;
  padding: 0.75rem 1rem;
  border-left: 4px solid var(--accent, var(--teal));
  background: var(--paper-warm);
  border-radius: 0 8px 8px 0;
  color: var(--ink-soft);
}

.md-prose :deep(blockquote p) {
  margin: 0.3rem 0;
}

.md-prose :deep(table) {
  width: 100%;
  border-collapse: collapse;
  margin: 1rem 0;
  font-size: 0.95rem;
}

.md-prose :deep(th),
.md-prose :deep(td) {
  border: 1px solid var(--border);
  padding: 0.5rem 0.7rem;
  text-align: left;
  vertical-align: top;
}

.md-prose :deep(th) {
  background: var(--paper-warm);
  font-weight: 650;
}

.md-prose :deep(strong) {
  color: var(--ink);
}
</style>
